import { RefAttributes, useContext, useEffect } from "react"
import type { ErrorSchema } from "@rjsf/core"
import { SchemaFormRefProps } from "resha"
import { SchemaForm } from "universal-schema/lib"
import {
    Loader,
    OnboardingScreenContainer,
    SkipNavigationLink
} from "dash-styled"

import { OnboardingProgress } from "../../../../../Common/OnboardingProgress/OnboardingProgress"
import { SimpleEditor } from "../../../../../../../Common/SchemaForm/DocumentsExtraction/SimpleEditor"
import { VisualEditor as VisualEditorIsolated } from "../../../../../../../Common/SchemaForm/DocumentsExtraction/VisualEditor"
import { TemplateInstantiateStage } from "../../../../../../../../Models/Client/TemplateInstantiation"
import {
    DocumentsExtractionChildProps,
    InputFormData
} from "../../../../../../../Common/SchemaForm/DocumentsExtraction"
import { DocumentsExtractionContext } from "../../../../../../../Common/SchemaForm/DocumentsExtraction/DocumentsExtractionContext"
import { InputFormRenderProps } from "../../../../../../../Common/InputFormRender/InputFormRender"

const VisualEditor = ({
    demo,
    input,
    output,
    parameterSchema,
    errors,
    submitting,
    onBack,
    onForward,
    closeVisualEditor
}: {
    demo?: {
        active: boolean
        toggle: () => void
    }
    input?: InputFormRenderProps & React.RefAttributes<SchemaFormRefProps>
    output: Omit<InputFormRenderProps, "schema" | "uiSchema"> &
        RefAttributes<SchemaFormRefProps>
    parameterSchema?: SchemaForm
    errors?: ErrorSchema
    submitting: boolean
    onBack?: () => void
    onForward?: () => void
    closeVisualEditor: () => void
}) =>
    input ? (
        <VisualEditorIsolated
            isOnboarding
            demo={demo}
            input={input}
            output={output}
            parameterSchema={parameterSchema}
            errors={errors}
            onBack={onBack}
            onForward={submitting || errors ? undefined : onForward}
            closeVisualEditor={closeVisualEditor}
        />
    ) : null

export const DocumentExtractionParameters = ({
    submitting,
    onChange,
    onForward,
    onBack,
    input,
    output,
    parameterSchema,
    demo,
    errors,
    visualEditor
}: DocumentsExtractionChildProps<{}>) => {
    const [isVisualEditor, openVisualEditor, closeVisualEditor] = visualEditor

    const { setAnnotationsOutputList } = useContext(DocumentsExtractionContext)

    const documentUrl = (input?.formData as InputFormData)?.documentUrl

    useEffect(() => {
        setAnnotationsOutputList({
            url: documentUrl || "",
            annotations: {}
        })
    }, [documentUrl, setAnnotationsOutputList])

    return isVisualEditor ? (
        <>
            <SkipNavigationLink />
            <OnboardingProgress
                noMargin
                currentStage={TemplateInstantiateStage.setParameters}
                back={onBack !== undefined}
                onBack={onBack}
            />
            {submitting ? (
                <Loader />
            ) : (
                <div
                    id="maincontent"
                    className="w-full"
                    style={{ height: "calc(100% - 70px)" }}
                >
                    <VisualEditor
                        demo={demo}
                        input={input}
                        output={{
                            ...output,
                            onChange
                        }}
                        parameterSchema={parameterSchema}
                        errors={errors}
                        submitting={submitting}
                        onBack={onBack}
                        onForward={onForward}
                        closeVisualEditor={closeVisualEditor}
                    />
                </div>
            )}
        </>
    ) : (
        <OnboardingScreenContainer
            forward
            celebrate={false}
            onForward={submitting || errors ? undefined : onForward}
        >
            <OnboardingProgress
                currentStage={TemplateInstantiateStage.setParameters}
                back={onBack !== undefined}
                onBack={onBack}
            />
            <div id="maincontent" className="w-full">
                {submitting ? (
                    <Loader />
                ) : (
                    <SimpleEditor
                        demo={demo}
                        input={input}
                        output={{
                            ...output,
                            onChange
                        }}
                        parameterSchema={parameterSchema}
                        errors={errors}
                        onBack={onBack}
                        openVisualEditor={openVisualEditor}
                    />
                )}
            </div>
        </OnboardingScreenContainer>
    )
}
